import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useCart } from '../../hooks/useCart';
import { useAuth } from '../../hooks/useAuth';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { ApiClient } from '../../services/api';
import { API_URL } from '../../utils/config';
import { trackPurchase, trackConversion } from '../../utils/googleTag';

interface VerificationResponse {
  success: boolean;
  status: string;
  paymentStatus: string;
  customerEmail: string;
  items: Array<{
    marketName: string;
    marketId: string | null;
  }>;
  sessionId: string;
  subscriptionId: string | null;
  lineItems: Array<{
    description: string;
    priceId: string;
    amount: number;
    currency: string;
    quantity: number;
  }>;
}

const CheckoutSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const { isAuthenticated } = useAuth();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [loading, setLoading] = useState(true);
  const [verificationData, setVerificationData] = useState<VerificationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/checkout/success' } });
      return;
    }

    if (!sessionId) {
      setError('No checkout session found');
      setLoading(false);
      return;
    }

    verifySession();
  }, [sessionId, isAuthenticated]);

  const verifySession = async () => {
    try {
      setLoading(true);
      const apiClient = new ApiClient({ authenticatedFetch });
      const response = await apiClient.get<VerificationResponse>(`${API_URL}/api/store/checkout/verify/${sessionId}`);
      
      if (response.data.success) {
        setVerificationData(response.data);
        clearCart(); // Clear the cart on successful payment
        
        // Track purchase conversion for Google Ads
        const totalAmount = response.data.lineItems.reduce((sum, item) => sum + (item.amount * item.quantity), 0);
        const formattedAmount = totalAmount / 100; // Convert from cents to dollars
        
        // Track the purchase event
        trackPurchase(
          response.data.sessionId, // Transaction ID
          formattedAmount, // Total value
          response.data.lineItems[0]?.currency || 'USD', // Currency
          response.data.lineItems.map(item => ({
            item_id: item.priceId,
            item_name: item.description,
            price: item.amount / 100,
            quantity: item.quantity
          }))
        );
        
        // Track conversion for Google Ads (if conversion label is configured)
        // The conversion label would typically be provided by your Google Ads account
        // Example: trackConversion('YOUR_CONVERSION_LABEL', formattedAmount, 'USD');
        
      } else {
        setError('Payment was not completed successfully');
      }
    } catch (error: any) {
      console.error('Session verification error:', error);
      
      if (error.response?.status === 403) {
        setError('This checkout session does not belong to you');
      } else if (error.response?.status === 404) {
        setError('Checkout session not found');
      } else if (error.response?.status === 410) {
        setError('Your checkout session has expired');
      } else {
        setError('Unable to verify your payment. Please contact support.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Verifying your payment...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Button
            variant="contained"
            onClick={() => navigate('/global-market-reports')}
          >
            Return to Market Store
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <>
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            
            <Typography variant="h4" gutterBottom>
              ✅ Payment Successful!
            </Typography>
            
            <Typography variant="body1" color="text.secondary">
              Thank you for subscribing to:
            </Typography>
          </Box>

          <List sx={{ mb: 3 }}>
            {verificationData?.items.map((item, index) => (
              <React.Fragment key={index}>
                <ListItem>
                  <ListItemText
                    primary={`• ${item.marketName}`}
                    secondary={
                      verificationData.lineItems[index] && 
                      `${verificationData.lineItems[index].description}`
                    }
                  />
                </ListItem>
                {index < verificationData.items.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>

          <Alert severity="success" sx={{ mb: 3 }}>
            You now have immediate access to these market reports.
          </Alert>

          <Box sx={{ my: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Confirmation sent to:</strong> {verificationData?.customerEmail}
            </Typography>
            {verificationData?.subscriptionId && (
              <Typography variant="body2">
                <strong>Subscription ID:</strong> {verificationData.subscriptionId}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/subscriptions')}
              sx={{ minWidth: 200 }}
            >
              Access Your Markets
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/subscriptions')}
            >
              Manage Subscriptions
            </Button>
          </Box>

          <Typography variant="caption" display="block" sx={{ mt: 3, textAlign: 'center', color: 'text.secondary' }}>
            An invoice will be emailed to you shortly. You can also download it from your account settings.
          </Typography>
        </Paper>
      </Container>
    </>
  );
};

export default CheckoutSuccessPage;